import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Landmark,
} from "lucide-react";
import {
  contaBancariaService,
  contaBancariaValorService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  IconButton,
  SelectInput,
  TextAreaInput,
  TextInput,
  buttonPageActiveClass,
  buttonPageClass,
} from "../../components/ui";
import { formatCurrencyMoeda, formatarValorReal } from "../../utils/FormatarValores";
import {
  getMesAtualInput,
  competenciaParaInputMes,
  inputMesParaCompetencia,
  formatarCompetenciaMesAno,
} from "../../utils/FormatarCompetencia";

const FORM_INICIAL = {
  nome: "",
  descricao: "",
  agencia: "",
  conta: "",
};

const FORM_VALOR_INICIAL = {
  contaBancariaId: "",
  mesRef: getMesAtualInput(),
  valorMovimento: "",
  observacao: "",
};

const ContaBancaria = () => {
  const [abaAtiva, setAbaAtiva] = useState("contas");
  const [contas, setContas] = useState([]);
  const [valores, setValores] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);
  const [loadingValores, setLoadingValores] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [showFormValor, setShowFormValor] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [editingValorItem, setEditingValorItem] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filtrosValor, setFiltrosValor] = useState({
    contaBancariaId: "",
  });

  const [formData, setFormData] = useState(FORM_INICIAL);
  const [formValorData, setFormValorData] = useState(FORM_VALOR_INICIAL);

  const [paginacao, setPaginacao] = useState({ paginaAtual: 1, itensPorPagina: 6 });
  const [paginacaoValor, setPaginacaoValor] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadContas();
    loadValores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadContas = async () => {
    setLoadingContas(true);
    try {
      const data = await contaBancariaService.listar();
      const listaContas = Array.isArray(data) ? data : [];
      setContas(listaContas);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));

      if (!formValorData.contaBancariaId && listaContas.length > 0) {
        setFormValorData((prev) => ({
          ...prev,
          contaBancariaId: String(listaContas[0].id),
        }));
      }
    } catch (err) {
    } finally {
      setLoadingContas(false);
    }
  };

  const loadValores = async (filtros = filtrosValor) => {
    setLoadingValores(true);
    try {
      const response = await contaBancariaValorService.listar(
        filtros.contaBancariaId || undefined
      );
      const data = Array.isArray(response) ? response : [];

      setValores(data);
      setPaginacaoValor((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
      showError(err.message || "Erro ao carregar movimentações");
    } finally {
      setLoadingValores(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.nome.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    setProcessing(true);
    try {
      const dto = {
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim(),
        agencia: formData.agencia.trim(),
        conta: formData.conta.trim(),
      };

      if (editingItem) {
        await contaBancariaService.atualizar(editingItem.id, dto);
        showSuccess("Conta bancária atualizada com sucesso!");
      } else {
        await contaBancariaService.criar(dto);
        showSuccess("Conta bancária criada com sucesso!");
      }

      await loadContas();
      handleCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar conta bancária");
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nome: item.nome || "",
      descricao: item.descricao || "",
      agencia: item.agencia || "",
      conta: item.conta || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta conta bancária?",
    });

    if (!confirmed) {
      return;
    }

    setProcessing(true);
    try {
      await contaBancariaService.deletar(id);
      showSuccess("Conta bancária excluída com sucesso!");
      await loadContas();
    } catch (err) {
      showError(err.message || "Erro ao excluir conta bancária");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData(FORM_INICIAL);
  };

  const handleNew = () => {
    setEditingItem(null);
    setFormData(FORM_INICIAL);
    setShowForm(true);
  };

  const handleFiltroValorChange = (e) => {
    const { name, value } = e.target;
    setFiltrosValor((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPaginacaoValor((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const handleValorFormChange = (e) => {
    const { name, value } = e.target;
    setFormValorData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleValorMovimentoChange = (e) => {
    const value = e.target.value;
    const { formatted } = formatarValorReal(value);

    setFormValorData((prev) => ({
      ...prev,
      valorMovimento: value ? formatted : "",
    }));
  };

  const handleBuscarValores = async () => {
    await loadValores(filtrosValor);
  };

  const handleNewValor = () => {
    setEditingValorItem(null);
    setFormValorData({
      ...FORM_VALOR_INICIAL,
      contaBancariaId: filtrosValor.contaBancariaId || String(contas[0]?.id || ""),
    });
    setShowFormValor(true);
  };

  const handleEditValor = (item) => {
    const valorMovimentoNumero = Number(item.valorMovimento || 0);

    setEditingValorItem(item);
    setFormValorData({
      contaBancariaId: String(item.contaBancariaId || ""),
      mesRef: competenciaParaInputMes(item.mesRef),
      valorMovimento: formatarValorReal((valorMovimentoNumero * 100).toString()).formatted,
      observacao: item.observacao || "",
    });
    setShowFormValor(true);
  };

  const handleCancelValor = () => {
    setShowFormValor(false);
    setEditingValorItem(null);
    setFormValorData(FORM_VALOR_INICIAL);
  };

  const handleSubmitValor = async () => {
    const valorMovimentoNumerico =
      formValorData.valorMovimento === ""
        ? null
        : formatarValorReal(formValorData.valorMovimento).numeric;

    if (!formValorData.contaBancariaId) {
      showError("Conta bancária é obrigatória");
      return;
    }

    if (!formValorData.mesRef) {
      showError("Competência é obrigatória");
      return;
    }

    if (valorMovimentoNumerico === null || Number.isNaN(valorMovimentoNumerico)) {
      showError("Valor do movimento é obrigatório");
      return;
    }

    const payload = {
      contaBancariaId: Number(formValorData.contaBancariaId),
      mesRef: inputMesParaCompetencia(formValorData.mesRef),
      valorMovimento: valorMovimentoNumerico,
      observacao: formValorData.observacao.trim(),
    };

    setProcessing(true);
    try {
      if (editingValorItem) {
        await contaBancariaValorService.atualizar(editingValorItem.id, payload);
        showSuccess("Movimentação atualizada com sucesso!");
      } else {
        await contaBancariaValorService.criar(payload);
        showSuccess("Movimentação criada com sucesso!");
      }

      await loadValores(filtrosValor);
      handleCancelValor();
    } catch (err) {
      showError(err.message || "Erro ao salvar movimentação");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteValor = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta movimentação?",
    });

    if (!confirmed) {
      return;
    }

    setProcessing(true);
    try {
      await contaBancariaValorService.deletar(id);
      showSuccess("Movimentação excluída com sucesso!");
      await loadValores(filtrosValor);
    } catch (err) {
      showError(err.message || "Erro ao excluir movimentação");
    } finally {
      setProcessing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const contasFiltradas = contas.filter((item) => {
    const termo = searchTerm.trim().toLowerCase();
    if (!termo) return true;
    return [item.nome, item.descricao, item.agencia, item.conta].some((campo) =>
      (campo || "").toLowerCase().includes(termo)
    );
  });

  const totalRegistros = contasFiltradas.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const contasPaginadas = contasFiltradas.slice(indiceInicial, indiceFinal);

  const totalRegistrosValor = valores.length;
  const totalPaginasValor = Math.ceil(totalRegistrosValor / paginacaoValor.itensPorPagina);
  const indiceInicialValor = (paginacaoValor.paginaAtual - 1) * paginacaoValor.itensPorPagina;
  const indiceFinalValor = indiceInicialValor + paginacaoValor.itensPorPagina;
  const valoresPaginados = valores.slice(indiceInicialValor, indiceFinalValor);

  const irParaPrimeiraPagina = () => setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  const irParaUltimaPagina = () => setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  const irParaPaginaAnterior = () => setPaginacao((prev) => ({ ...prev, paginaAtual: Math.max(1, prev.paginaAtual - 1) }));
  const irParaProximaPagina = () => setPaginacao((prev) => ({ ...prev, paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1) }));
  const irParaPagina = (n) => setPaginacao((prev) => ({ ...prev, paginaAtual: n }));
  const irParaPrimeiraPaginaValor = () => setPaginacaoValor((prev) => ({ ...prev, paginaAtual: 1 }));
  const irParaUltimaPaginaValor = () =>
    setPaginacaoValor((prev) => ({ ...prev, paginaAtual: totalPaginasValor }));
  const irParaPaginaAnteriorValor = () =>
    setPaginacaoValor((prev) => ({ ...prev, paginaAtual: Math.max(1, prev.paginaAtual - 1) }));
  const irParaProximaPaginaValor = () =>
    setPaginacaoValor((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginasValor, prev.paginaAtual + 1),
    }));
  const irParaPaginaValor = (n) => setPaginacaoValor((prev) => ({ ...prev, paginaAtual: n }));

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxVisiveis = 5;
    let inicio = Math.max(1, paginacao.paginaAtual - Math.floor(maxVisiveis / 2));
    let fim = Math.min(totalPaginas, inicio + maxVisiveis - 1);
    if (fim - inicio < maxVisiveis - 1) inicio = Math.max(1, fim - maxVisiveis + 1);
    for (let i = inicio; i <= fim; i++) paginas.push(i);
    return paginas;
  };

  const gerarNumerosPaginasValor = () => {
    const paginas = [];
    const maxVisiveis = 5;
    let inicio = Math.max(1, paginacaoValor.paginaAtual - Math.floor(maxVisiveis / 2));
    let fim = Math.min(totalPaginasValor, inicio + maxVisiveis - 1);
    if (fim - inicio < maxVisiveis - 1) inicio = Math.max(1, fim - maxVisiveis + 1);
    for (let i = inicio; i <= fim; i++) paginas.push(i);
    return paginas;
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Landmark size={24} />
          Contas Bancárias
        </h1>
        <p className="text-gray-600">Gerencie as contas bancárias do sistema</p>
      </div>

      <div className="bg-white rounded-xl border p-1 inline-flex mb-6">
        <button
          onClick={() => setAbaAtiva("contas")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            abaAtiva === "contas" ? "bg-blue-600 text-white" : "text-gray-700"
          }`}
        >
          Contas Bancárias
        </button>
        <button
          onClick={() => setAbaAtiva("valores")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            abaAtiva === "valores" ? "bg-blue-600 text-white" : "text-gray-700"
          }`}
        >
          Valores da Conta
        </button>
      </div>

      {abaAtiva === "contas" && (
        <>
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <TextInput
            type="text"
            placeholder="Pesquisar por nome, descrição, agência ou conta..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>

        <Button
          onClick={handleNew}
          disabled={processing}
          icon={Plus}
        >
          Nova Conta Bancária
        </Button>
      </div>

      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? "Editar Conta Bancária" : "Nova Conta Bancária"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nome" required htmlFor="nome">
              <TextInput
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Ex.: Banco do Brasil"
                maxLength={100}
              />
            </FormField>

            <FormField label="Agência" htmlFor="agencia">
              <TextInput
                type="text"
                id="agencia"
                name="agencia"
                value={formData.agencia}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Ex.: 1234-5"
                maxLength={20}
              />
            </FormField>

            <FormField label="Conta" htmlFor="conta">
              <TextInput
                type="text"
                id="conta"
                name="conta"
                value={formData.conta}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Ex.: 12345-6"
                maxLength={30}
              />
            </FormField>

            <FormField className="md:col-span-2" label="Descrição" htmlFor="descricao">
              <TextAreaInput
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                placeholder="Descrição da conta bancária"
                maxLength={500}
              />
            </FormField>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleSubmit}
              disabled={processing || !formData.nome.trim()}
              variant="success"
              icon={Save}
            >
              {processing ? "Salvando..." : "Salvar"}
            </Button>

            <Button
              onClick={handleCancel}
              variant="secondary"
              icon={X}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center gap-4">
          <h3 className="text-lg font-semibold">Lista de Contas Bancárias</h3>
          <span className="text-sm text-gray-500">{totalRegistros} registro(s) encontrado(s)</span>
        </div>

        {loadingContas && contas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Carregando contas bancárias...</div>
        ) : totalRegistros === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? "Nenhuma conta bancária encontrada" : "Nenhuma conta bancária cadastrada"}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agência</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conta</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contasPaginadas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.nome}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {item.agencia || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {item.conta || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                        <div className="truncate" title={item.descricao || ""}>
                          {item.descricao || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <IconButton
                            onClick={() => handleEdit(item)}
                            disabled={processing}
                            icon={Edit2}
                            variant="info"
                            label="Editar"
                          />
                          <IconButton
                            onClick={() => handleDelete(item.id)}
                            disabled={processing}
                            icon={Trash2}
                            variant="danger"
                            label="Excluir"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} a {Math.min(indiceFinal, totalRegistros)} de {totalRegistros} registros
                  </div>

                  <div className="flex items-center gap-2">
                    <IconButton
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      icon={ChevronsLeft}
                      label="Primeira página"
                    />

                    <IconButton
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      icon={ChevronLeft}
                      label="Página anterior"
                    />

                    <div className="flex gap-1">
                      {gerarNumerosPaginas().map((pagina) => (
                        <Button
                          key={pagina}
                          onClick={() => irParaPagina(pagina)}
                          variant="ghost"
                          size="sm"
                          className={
                            pagina === paginacao.paginaAtual
                              ? buttonPageActiveClass
                              : buttonPageClass
                          }
                        >
                          {pagina}
                        </Button>
                      ))}
                    </div>

                    <IconButton
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      icon={ChevronRight}
                      label="Próxima página"
                    />

                    <IconButton
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      icon={ChevronsRight}
                      label="Última página"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      {abaAtiva === "valores" && (
        <>
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
            <SelectInput
              name="contaBancariaId"
              value={filtrosValor.contaBancariaId}
              onChange={handleFiltroValorChange}
            >
              <option value="">Todas as contas</option>
              {contas.map((conta) => (
                <option key={conta.id} value={conta.id}>
                  {conta.nome}
                </option>
              ))}
            </SelectInput>

            <Button
              onClick={handleBuscarValores}
              icon={RefreshCw}
              disabled={loadingValores}
            >
              Buscar
            </Button>

            <Button
              onClick={handleNewValor}
              disabled={processing || contas.length === 0}
              variant="success"
              icon={Plus}
            >
              Novo Valor
            </Button>
          </div>

          {showFormValor && (
            <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
              <h3 className="text-lg font-semibold mb-4">
                {editingValorItem ? "Editar Valor da Conta" : "Novo Valor da Conta"}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Conta Bancária" required htmlFor="contaBancariaId">
                  <SelectInput
                    id="contaBancariaId"
                    name="contaBancariaId"
                    value={formValorData.contaBancariaId}
                    onChange={handleValorFormChange}
                  >
                    <option value="">Selecione...</option>
                    {contas.map((conta) => (
                      <option key={conta.id} value={conta.id}>
                        {conta.nome}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label="Data de referência" required htmlFor="mesRef">
                  <TextInput
                    type="month"
                    id="mesRef"
                    name="mesRef"
                    value={formValorData.mesRef}
                    onChange={handleValorFormChange}
                  />
                </FormField>

                <FormField label="Valor do Movimento" required htmlFor="valorMovimento">
                  <TextInput
                    type="text"
                    id="valorMovimento"
                    name="valorMovimento"
                    value={formValorData.valorMovimento}
                    onChange={handleValorMovimentoChange}
                    placeholder="0,00"
                  />
                </FormField>

                <FormField className="md:col-span-2" label="Observação" htmlFor="observacao">
                  <TextAreaInput
                    id="observacao"
                    name="observacao"
                    value={formValorData.observacao}
                    onChange={handleValorFormChange}
                    maxLength={500}
                  />
                </FormField>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleSubmitValor}
                  disabled={processing}
                  variant="success"
                  icon={Save}
                >
                  {processing ? "Salvando..." : "Salvar"}
                </Button>

                <Button
                  onClick={handleCancelValor}
                  variant="secondary"
                  icon={X}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center gap-4">
              <h3 className="text-lg font-semibold">Lista de Valores por Conta</h3>
              <span className="text-sm text-gray-500">
                {totalRegistrosValor} registro(s) encontrado(s)
              </span>
            </div>

            {loadingValores && valores.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Carregando movimentações de conta bancária...
              </div>
            ) : totalRegistrosValor === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nenhuma movimentação de conta bancária encontrada
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conta
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Competência
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Observação
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {valoresPaginados.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.contaBancariaNome ||
                              contas.find((conta) => Number(conta.id) === Number(item.contaBancariaId))
                                ?.nome ||
                              "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {formatarCompetenciaMesAno(item.mesRef)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                            {formatCurrencyMoeda(Number(item.valorMovimento || 0))}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                            <div className="truncate" title={item.observacao || ""}>
                              {item.observacao || "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEditValor(item)}
                                disabled={processing}
                                className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteValor(item.id)}
                                disabled={processing}
                                className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPaginasValor > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Mostrando {indiceInicialValor + 1} a {Math.min(indiceFinalValor, totalRegistrosValor)} de {totalRegistrosValor} registros
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={irParaPrimeiraPaginaValor}
                          disabled={paginacaoValor.paginaAtual === 1}
                          className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          title="Primeira página"
                        >
                          <ChevronsLeft size={16} />
                        </button>

                        <button
                          onClick={irParaPaginaAnteriorValor}
                          disabled={paginacaoValor.paginaAtual === 1}
                          className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          title="Página anterior"
                        >
                          <ChevronLeft size={16} />
                        </button>

                        <div className="flex gap-1">
                          {gerarNumerosPaginasValor().map((pagina) => (
                            <button
                              key={pagina}
                              onClick={() => irParaPaginaValor(pagina)}
                              className={`px-3 py-2 rounded-md text-sm font-medium ${
                                pagina === paginacaoValor.paginaAtual
                                  ? "bg-blue-600 text-white"
                                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {pagina}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={irParaProximaPaginaValor}
                          disabled={paginacaoValor.paginaAtual === totalPaginasValor}
                          className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          title="Próxima página"
                        >
                          <ChevronRight size={16} />
                        </button>

                        <button
                          onClick={irParaUltimaPaginaValor}
                          disabled={paginacaoValor.paginaAtual === totalPaginasValor}
                          className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          title="Última página"
                        >
                          <ChevronsRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default ContaBancaria;
